'use client'

import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Star, Quote, ChevronLeft, ChevronRight, MessageSquareQuote } from 'lucide-react'
import SmartImage from './ui/SmartImage'
import { siteImages } from '@/data/siteImages'

const testimonials = [
  {
    name: 'Sarah Mitchell',
    role: 'CTO, FinEdge',
    avatar: siteImages.avatars.a3,
    rating: 5,
    quote: 'They rebuilt our platform from the ground up and shipped ahead of schedule. The engineering quality and communication were exceptional throughout.',
  },
  {
    name: 'David Chen',
    role: 'Founder, ShopNova',
    avatar: siteImages.avatars.a2,
    rating: 5,
    quote: 'Our e-commerce conversion jumped 38% after the redesign. The team understood our business, not just the code.',
  },
  {
    name: 'Aisha Rahman',
    role: 'Product Lead, MediCare+',
    avatar: siteImages.avatars.a5,
    rating: 5,
    quote: 'HIPAA-compliant, fast, and beautifully designed. They delivered a healthcare product our clinicians actually enjoy using.',
  },
  {
    name: 'Marco Rossi',
    role: 'VP Engineering, LogiFlow',
    avatar: siteImages.avatars.a1,
    rating: 5,
    quote: 'Scalable architecture, clean DevOps pipelines, and real partnership. Easily the best vendor relationship we have had.',
  },
]

export default function Testimonials() {
  const [index, setIndex] = useState(0)
  const [dir, setDir] = useState(1)

  const go = useCallback((d: number) => {
    setDir(d)
    setIndex((i) => (i + d + testimonials.length) % testimonials.length)
  }, [])

  useEffect(() => {
    const t = setInterval(() => go(1), 6000)
    return () => clearInterval(t)
  }, [go])

  const t = testimonials[index]

  return (
    <section className="py-20 sm:py-28 bg-gradient-to-b from-white to-sky-50/40 relative overflow-hidden">
      <div className="absolute -top-10 right-10 w-72 h-72 bg-sky-100/40 rounded-full blur-3xl" />
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-sky-100 text-sky-600 text-sm font-medium mb-4">
            <MessageSquareQuote className="w-4 h-4" /> Client Stories
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            What Our <span className="gradient-text">Clients Say</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-500 max-w-2xl mx-auto">
            Trusted by founders and teams across the globe
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          <div className="relative bg-white rounded-3xl shadow-xl border border-sky-100/60 p-8 sm:p-12 overflow-hidden">
            <Quote className="absolute top-6 right-8 w-20 h-20 text-sky-50" />
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={index}
                custom={dir}
                initial={{ opacity: 0, x: dir * 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: dir * -60 }}
                transition={{ duration: 0.4 }}
                className="relative z-10"
              >
                <div className="flex gap-1 mb-5">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-lg sm:text-xl text-slate-700 leading-relaxed mb-8">“{t.quote}”</p>
                <div className="flex items-center gap-4">
                  <div className="relative w-14 h-14 rounded-full overflow-hidden ring-2 ring-sky-100">
                    <SmartImage src={t.avatar} alt={t.name} fill sizes="56px" className="object-cover" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">{t.name}</p>
                    <p className="text-sm text-sky-600">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => go(-1)}
              aria-label="Previous testimonial"
              className="w-10 h-10 rounded-full bg-white border border-sky-100 shadow-sm flex items-center justify-center text-slate-600 hover:text-sky-600 hover:border-sky-300 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setDir(i > index ? 1 : -1); setIndex(i) }}
                  aria-label={`Go to testimonial ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${i === index ? 'w-6 bg-sky-500' : 'w-2 bg-sky-200 hover:bg-sky-300'}`}
                />
              ))}
            </div>
            <button
              onClick={() => go(1)}
              aria-label="Next testimonial"
              className="w-10 h-10 rounded-full bg-white border border-sky-100 shadow-sm flex items-center justify-center text-slate-600 hover:text-sky-600 hover:border-sky-300 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}
